import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { LevelPie, MonthlyLessonsBar, SubmissionPie, TopRecordsBar } from './components/charts';

interface ProfileRow {
  id: string;
  full_name: string;
  active: boolean;
}

interface StudentRow {
  id: string;
  level: string | null;
  performance: string | null;
  attendance: string | null;
}

interface LessonRow {
  date: string | null;
}

interface RecordRow {
  title: string | null;
  views: number | null;
}

interface SubmissionRow {
  student_id: string | null;
  assignment_id: string | null;
  status: string;
}

const MONTH_NAMES = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const SUBMISSION_COLORS: Record<string, string> = {
  teslim_edildi: '#f59e0b',
  kontrol_edildi: '#10b981',
  eksik: '#ef4444',
  yeniden: '#f97316',
};

const SUBMISSION_LABELS: Record<string, string> = {
  teslim_edildi: 'Teslim Edildi',
  kontrol_edildi: 'Kontrol Edildi',
  eksik: 'Eksik',
  yeniden: 'Yeniden Yapılmalı',
};

function perfBadge(val: string | null): string | null {
  if (!val) return null;
  const v = val.toLowerCase();
  if (['iyi', 'çok iyi', 'yüksek', 'başarılı'].some((k) => v.includes(k))) return 'bg-emerald-100 text-emerald-700';
  if (['orta', 'gelişmekte', 'gelişen'].some((k) => v.includes(k))) return 'bg-amber-100 text-amber-700';
  if (['zayıf', 'düşük', 'riskli'].some((k) => v.includes(k))) return 'bg-red-100 text-red-700';
  return 'bg-secondary-100 text-secondary-700';
}

function attendBadge(val: string | null): string | null {
  if (!val) return null;
  const v = val.toLowerCase();
  if (['düzenli', 'tam', 'iyi'].some((k) => v.includes(k))) return 'bg-emerald-100 text-emerald-700';
  if (['orta'].some((k) => v.includes(k))) return 'bg-amber-100 text-amber-700';
  if (['düzensiz', 'zayıf'].some((k) => v.includes(k))) return 'bg-red-100 text-red-700';
  return 'bg-secondary-100 text-secondary-700';
}

export default function Raporlar() {
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const [records, setRecords] = useState<RecordRow[]>([]);
  const [submissions, setSubmissions] = useState<SubmissionRow[]>([]);
  const [assignmentsCount, setAssignmentsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchAll() {
    setLoading(true);
    setError(null);
    try {
      const [
        { data: studentRows, error: studentsError },
        { data: profileRows, error: profilesError },
        { data: lessonRows, error: lessonsError },
        { data: recordRows, error: recordsError },
        { data: submissionRows, error: submissionsError },
        { count: assignmentCount, error: assignmentsError },
      ] = await Promise.all([
        supabase.from('zehrahoca_students').select('id, level, performance, attendance'),
        supabase.from('zehrahoca_profiles').select('id, full_name, active').eq('role', 'ogrenci'),
        supabase.from('zehrahoca_lessons').select('date'),
        supabase.from('zehrahoca_lesson_records').select('title, views').order('views', { ascending: false }),
        supabase.from('zehrahoca_assignment_submissions').select('student_id, assignment_id, status'),
        supabase.from('zehrahoca_assignments').select('id', { count: 'exact', head: true }),
      ]);

      if (studentsError) throw studentsError;
      if (profilesError) throw profilesError;
      if (lessonsError) throw lessonsError;
      if (recordsError) throw recordsError;
      if (submissionsError) throw submissionsError;
      if (assignmentsError) throw assignmentsError;

      setStudents((studentRows ?? []) as StudentRow[]);
      setProfiles((profileRows ?? []) as ProfileRow[]);
      setLessons((lessonRows ?? []) as LessonRow[]);
      setRecords((recordRows ?? []) as RecordRow[]);
      setSubmissions((submissionRows ?? []) as SubmissionRow[]);
      setAssignmentsCount(assignmentCount ?? 0);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchAll();
  }, []);

  const profileMap = useMemo(() => {
    const m: Record<string, string> = {};
    profiles.forEach((p) => { m[p.id] = p.full_name; });
    return m;
  }, [profiles]);

  const kpi = useMemo(() => {
    const totalViews = records.reduce((s, r) => s + Number(r.views ?? 0), 0);
    const submitted = submissions.filter((s) => s.status === 'teslim_edildi' || s.status === 'kontrol_edildi').length;
    const total = submissions.length;
    const completionRate = total > 0 ? Math.round((submitted / total) * 100) : 0;
    const activeStudents = profiles.filter((p) => p.active).length;
    const passiveStudents = profiles.filter((p) => !p.active).length;
    return { totalStudents: students.length, activeStudents, passiveStudents, totalLessons: lessons.length, totalViews, submitted, total, completionRate };
  }, [students, profiles, lessons, records, submissions]);

  const levelData = useMemo(() => {
    const counts: Record<string, number> = {};
    students.forEach((s) => {
      const key = s.level ?? 'Belirtilmemiş';
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [students]);

  const monthlyData = useMemo(() => {
    const counts: Record<string, number> = {};
    lessons.forEach((l) => {
      if (!l.date) return;
      const d = new Date(l.date);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-6)
      .map(([key, value]) => {
        const [year, month] = key.split('-');
        return { name: `${MONTH_NAMES[Number(month)]}`, value };
      });
  }, [lessons]);

  const topRecords = useMemo(() => {
    return records.slice(0, 5).map((r) => ({
      name: r.title && r.title.length > 20 ? `${r.title.slice(0, 20)}…` : (r.title ?? 'Kayıt'),
      value: Number(r.views ?? 0),
    }));
  }, [records]);

  const submissionData = useMemo(() => {
    const counts: Record<string, number> = {};
    submissions.forEach((s) => {
      counts[s.status] = (counts[s.status] ?? 0) + 1;
    });
    return Object.entries(counts).map(([status, value]) => ({
      name: SUBMISSION_LABELS[status] ?? status,
      value,
      color: SUBMISSION_COLORS[status] ?? 'oklch(var(--secondary-400))',
    }));
  }, [submissions]);

  const studentTable = useMemo(() => {
    return students.map((s) => {
      const studentSubs = submissions.filter((sub) => sub.student_id === s.id);
      const submitted = studentSubs.filter((sub) => sub.status === 'teslim_edildi' || sub.status === 'kontrol_edildi').length;
      const rate = assignmentsCount > 0 ? Math.round((submitted / assignmentsCount) * 100) : 0;
      return {
        id: s.id,
        name: profileMap[s.id] ?? 'Bilinmeyen Öğrenci',
        level: s.level,
        performance: s.performance,
        attendance: s.attendance,
        submitted,
        assignmentsCount,
        rate: Math.min(rate, 100),
      };
    }).sort((a, b) => b.rate - a.rate);
  }, [students, submissions, assignmentsCount, profileMap]);

  if (loading) {
    return (
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
        <p className="mt-4 text-sm text-foreground-500">Raporlar hazırlanıyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl py-10">
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <i className="ri-error-warning-line text-2xl text-red-500" />
          <p className="mt-2 text-sm font-medium text-red-700">Raporlar yüklenirken bir hata oluştu.</p>
          <p className="mt-1 text-xs text-red-500">{error}</p>
          <button type="button" onClick={fetchAll} className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer">
            <i className="ri-refresh-line" /> Yeniden Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Raporlar</h1>
        <p className="mt-1 text-sm text-foreground-600">Öğrenci başarısı ve ders performansının genel görünümü.</p>
      </div>

      {/* KPI kartları */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
            <i className="ri-team-line text-xl" />
          </div>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Toplam Öğrenci</p>
          <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">{kpi.totalStudents}</p>
          <p className="mt-1 text-xs text-foreground-500">{kpi.activeStudents} aktif · {kpi.passiveStudents} pasif</p>
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-600">
            <i className="ri-book-open-line text-xl" />
          </div>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Toplam Ders</p>
          <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">{kpi.totalLessons}</p>
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500">
            <i className="ri-play-circle-line text-xl" />
          </div>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Kayıt İzlenme</p>
          <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">{kpi.totalViews.toLocaleString('tr-TR')}</p>
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
            <i className="ri-task-line text-xl" />
          </div>
          <p className="mt-3 text-xs font-semibold text-foreground-500">Ödev Tamamlama</p>
          <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">%{kpi.completionRate}</p>
          <p className="text-xs text-foreground-500">{kpi.submitted} / {kpi.total} teslim</p>
        </div>
      </div>

      {/* Grafikler */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-background-200 bg-white p-5">
          <h3 className="font-heading text-base font-bold text-foreground-950">Öğrenci Seviye Dağılımı</h3>
          <p className="mb-4 text-xs text-foreground-500">Öğrencilerin sınıf seviyelerine göre dağılımı</p>
          <LevelPie data={levelData} />
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <h3 className="font-heading text-base font-bold text-foreground-950">Aylık Ders Sayısı</h3>
          <p className="mb-4 text-xs text-foreground-500">Son 6 ayda işlenen dersler</p>
          <MonthlyLessonsBar data={monthlyData} />
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <h3 className="font-heading text-base font-bold text-foreground-950">En Çok İzlenen Kayıtlar</h3>
          <p className="mb-4 text-xs text-foreground-500">İzlenme sayısına göre ilk 5 ders kaydı</p>
          <TopRecordsBar data={topRecords} />
        </div>

        <div className="rounded-xl border border-background-200 bg-white p-5">
          <h3 className="font-heading text-base font-bold text-foreground-950">Ödev Teslim Durumu</h3>
          <p className="mb-4 text-xs text-foreground-500">Tüm ödev teslimlerinin durumu</p>
          <SubmissionPie data={submissionData} />
        </div>
      </div>

      {/* Öğrenci başarı tablosu */}
      <div className="overflow-hidden rounded-xl border border-background-200 bg-white">
        <div className="border-b border-background-200 px-5 py-4">
          <h3 className="font-heading text-base font-bold text-foreground-950">Öğrenci Başarı Özeti</h3>
          <p className="text-xs text-foreground-500">Öğrenci bazında ödev tamamlama oranı</p>
        </div>
        {studentTable.length === 0 ? (
          <div className="p-10 text-center text-sm text-foreground-500">Henüz öğrenci verisi bulunmuyor.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50 text-left text-xs font-semibold text-foreground-500">
                  <th className="px-5 py-3.5">Öğrenci</th>
                  <th className="px-5 py-3.5">Seviye</th>
                  <th className="px-5 py-3.5">Performans</th>
                  <th className="px-5 py-3.5">Devam</th>
                  <th className="px-5 py-3.5">Ödev Tamamlama</th>
                </tr>
              </thead>
              <tbody>
                {studentTable.map((s) => {
                  const perf = perfBadge(s.performance);
                  const attend = attendBadge(s.attendance);
                  const initial = s.name.trim().charAt(0).toUpperCase();
                  return (
                    <tr key={s.id} className="border-b border-background-100 last:border-0 hover:bg-background-50/60">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                            {initial}
                          </div>
                          <span className="font-semibold text-foreground-950">{s.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-foreground-600">{s.level ?? '—'}</td>
                      <td className="px-5 py-4">
                        {perf ? (
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${perf}`}>{s.performance}</span>
                        ) : <span className="text-foreground-400">—</span>}
                      </td>
                      <td className="px-5 py-4">
                        {attend ? (
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${attend}`}>{s.attendance}</span>
                        ) : <span className="text-foreground-400">—</span>}
                      </td>
                      <td className="px-5 py-4">
                        <div className="max-w-[200px]">
                          <div className="mb-1.5 flex items-center justify-between text-xs">
                            <span className="text-foreground-600">{s.submitted}/{s.assignmentsCount} ödev</span>
                            <span className="font-semibold text-foreground-500">%{s.rate}</span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-background-100">
                            <div
                              className={`h-full rounded-full ${s.rate >= 80 ? 'bg-emerald-500' : s.rate >= 50 ? 'bg-accent-500' : 'bg-red-500'}`}
                              style={{ width: `${s.rate}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}